import { Product } from '@shared/components/ui/Product';
import { useTranslation } from 'react-i18next';

interface ProductToLocate {
  id: string;
  name: string;
  code: string;
  quantity: number;
  totalQuantity: number;
  location?: string;
  image?: string;
}

interface ProductsToLocateSectionProps {
  products: ProductToLocate[];
  onProductLocate: (productId: string, locationId: number) => void;
  productLocationMap?: Record<number, string[]>;
  searchQuery?: string;
}

export const ProductsToLocateSection = ({
  products,
  productLocationMap = {},
  searchQuery = '',
}: ProductsToLocateSectionProps) => {
  const { t } = useTranslation('receiveModule');

  // Agrupar productos por ubicación usando el mapa de ubicaciones
  const groupedProducts = products.reduce(
    (acc, product) => {
      // 🎯 Obtener la ubicación real del producto usando el mapa
      const productId = parseInt(product.id);
      const productLocations = productLocationMap[productId] || [];
      // ✅ Tomar la primera ubicación si hay múltiples
      const realLocation = productLocations.length > 0 ? productLocations[0] : product.location || 'Sin ubicación';

      // (Sin logs de depuración)

      if (!acc[realLocation]) {
        acc[realLocation] = [];
      }
      acc[realLocation].push(product);
      return acc;
    },
    {} as Record<string, ProductToLocate[]>,
  );

  if (products.length === 0) {
    const isSearching = Boolean(searchQuery && searchQuery.trim());
    return (
      <div className="sm:mt-16 text-center text-complementary-text text-sm">
        {isSearching ? t('items.search.no_results.title') : t('ui.no_products_to_locate')}
      </div>
    );
  }

  return (
    <div className="space-y-4 mt-4 sm:max-h-96 sm:overflow-y-auto">
      {Object.entries(groupedProducts).map(([location, locationProducts]) => (
        <div key={location} className="space-y-3">
          {/* Productos de esta ubicación */}
          <div className="space-y-2">
            {locationProducts.map((product) => (
              <div key={product.id}>
                {/* Título de ubicaciones (todas las del producto separadas por coma) */}
                <h3 className="text-title text-sm font-medium mb-0">
                  {(() => {
                    const ids = productLocationMap[parseInt(product.id)] || [];
                    return ids.length > 0 ? ids.join(', ') : product.location || 'Sin ubicación';
                  })()}
                </h3>
                <div className="flex items-center justify-between py-3 border-b border-card-border !mt-0">
                  {/* Componente Product */}
                  <Product
                    id={parseInt(product.id)}
                    src={product.image || '/imagen/example.webp'}
                    name={product.name}
                    code={product.code}
                    className="flex-1"
                  />

                  {/* Cantidad y botón de ubicar */}
                  <div className="flex items-center gap-3">
                    {/* Cantidad */}
                    <div className="flex items-center gap-2 bg-card-border light:bg-[#3D4A5D] rounded-full px-3 py-1">
                      <span className="text-xs text-title light:text-white">{product.quantity}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
